import { Clipboard } from '@angular/cdk/clipboard';
import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, Renderer2, RendererFactory2, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { NgbModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { Actions, ofType } from '@ngrx/effects';
import { DocumentFieldEmitterService } from 'app/global/documentfield-emitter.service';
import { GlobalService } from 'app/global/global.service';
import { IntegrationService } from 'app/global/integrations/integrations.service';
import { PermissionsService } from 'app/global/permissions/permissions.service';
import { Field } from 'app/interfaces/fields';
import { FieldRenderService } from 'app/main/pages/edit-template/field-render.service';
import { AbstractUpdateFieldsComponent } from 'app/main/pages/general-editor-tabs/fields-builder-service/abstract-update-fields-data.component';
import { FieldsBuilderService } from 'app/main/pages/general-editor-tabs/fields-builder-service/fields-builder.service';
import { FieldsDataHelperService } from 'app/main/pages/general-editor-tabs/fields-builder-service/fields-data-helper.service';
import { DocumentTypeService, IFetchDocumentSectionLayoutSettings } from 'app/main/pages/settings/documenttype/document-type.service';
import { DocumentTypeEnum } from 'app/main/pages/templates/template.dto';
import { MomentPipe } from 'app/pipes/moment.pipe';
import * as moment from 'moment';
import { environment } from 'environments/environment';
import { delay, merge, of, Subject, Subscription, take, takeUntil } from 'rxjs';
import { IDocumentDetailRootDTO, Tag, VisibilityEnum } from '../../dto/document-details.dto';
import { IDocumentTypesDto } from '../../dto/document-listing.dto';
import { DocumentStatusesEnum } from '../../dto/document-statuses.enum';
import * as DocumentListingActions from '../../store/document-listing.actions';
import { DocumentListingFacade } from '../../store/document-listing.facade';
import { AddToContractControlModalComponent } from '../add-to-contract-control-modal/add-to-contract-control-modal.component';
import { documentPreviewColumns, IDocumentPreviewColumns } from '../custom-columns';
import { DocumentHelperFacade } from '../document-helper.facade';
import { DetailsSectionUpdateEvent } from './details-section/details-section.component';
import { SignatureSectionUpdateEvent } from './signature-request-section/signature-request-section.component';

@Component({
  selector: 'app-document-details',
  templateUrl: './document-details.component.html',
  styleUrl: './document-details.component.scss'
})

export class DocumentDetailsComponent extends AbstractUpdateFieldsComponent implements OnInit, OnDestroy, OnChanges {
  public documentForm: FormGroup;
  public topSectionForm: FormGroup;
  public signatureSectionForm: FormGroup;
  public detailsSectionForm: FormGroup;

  @ViewChild('tooltip') tooltip: NgbTooltip;
  @ViewChild('editableInput') editableInput: ElementRef;

  @Input() documentData: IDocumentDetailRootDTO;
  @Input() hasFullAccessToDocument: boolean;
  @Input() canEditDocument: boolean;
  @Input() fields_data: Field[] = [];

  public tags: Tag[] = [];
  public documentTypeSearch: string = '';
  public documentTypes$ = this._documentsFacade.documentTypes$;
  public categories$ = this._documentsFacade.categories$;
  public timerSubscription: Subscription;
  public selectedDocumentType: IDocumentTypesDto;
  public destroy$ = new Subject<void>();
  public documentPreviewColumns: IDocumentPreviewColumns[] = documentPreviewColumns;
  public documentTypeAllowedCategories: string;
  public documentStatusesEnum = DocumentStatusesEnum;
  public editableField: number;
  public clearButton: HTMLElement = document.createElement('button');
  public renderer: Renderer2;
  public isDueDateDisabled: boolean;
  public disableFlatpickr: ((date: Date) => boolean)[];
  private collapsedGroups: Record<string, boolean> = {};
  protected readonly VisibilityEnum = VisibilityEnum;
  private static readonly allowedDueDateChangeStatuses: string[] = [
    DocumentStatusesEnum.Sent,
    DocumentStatusesEnum.Delivered,
    DocumentStatusesEnum.Viewed,
    DocumentStatusesEnum.Expired,
  ];



  constructor(
    private _documentsFacade: DocumentListingFacade,
    private _copyToClipBoard$: Clipboard,
    private _actions$: Actions,
    public _globalService: GlobalService,
    private documentTypeService: DocumentTypeService,
    public _translocoService: TranslocoService,
    public _fieldsBuilderService: FieldsBuilderService,
    public _documentFieldsEmitter: DocumentFieldEmitterService,
    public _momentPipe$: MomentPipe,
    public _permissionService: PermissionsService,
    public _rendererFactory: RendererFactory2,
    public _integrationService: IntegrationService,
    public _modalService: NgbModal,
    public _documentHelperFacade: DocumentHelperFacade,
    private _fieldRenderService: FieldRenderService,
    public _fieldsDataHelperService: FieldsDataHelperService,
    private _documentListingFacade: DocumentListingFacade,
  private router: Router,
  private fb: FormBuilder
  ) {
  super(
      _globalService,
      _documentFieldsEmitter,
      _momentPipe$,
      _rendererFactory,
      _integrationService,
      _modalService,
      _fieldsBuilderService,
      _translocoService,
      _documentHelperFacade,
      _fieldsDataHelperService
    );
   }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.documentData || changes.hasFullAccessToDocument) {
      this.updateDueDateDisabled();
    }
  }

  isGroupCollapsed(groupName: string): boolean {
    return this.collapsedGroups[groupName] ?? true;
  }

  setGroupCollapsed(groupName: string, collapsed: boolean): void {
    this.collapsedGroups[groupName] = collapsed;
  }


  ngOnInit(): void {
    this.topSectionForm = this.fb.group({});
    this.signatureSectionForm = this.fb.group({});
    this.detailsSectionForm = this.fb.group({});
    this.documentForm = this.fb.group({
      top_section: this.topSectionForm,
      signature: this.signatureSectionForm,
      details: this.detailsSectionForm,
    }, {updateOn: 'change'});

    this._fieldRenderService.setMarkAsRenderedFields(this._globalService.fields_data);

    this.topSectionForm.get('document_type')?.valueChanges.subscribe((documentTypeId) => {
      if(!documentTypeId){
        console.log('No document type selected');
      }
    });
  }




  updateDocument(): void {
    this._documentListingFacade.saveDocumentEntitySetting(this.documentData.template.id, this.documentForm.value);
  }

  onDetailsSectionUpdate(event?: void | DetailsSectionUpdateEvent): void {
    if (event && event.action === 'dueDateChanged') {
      this.updateSigningReminderFromDueDate(event.data);
      this.updateDocumentDueDateFromValue(event.data);
      return;
    }
    this.updateDocument();
  }

  onSignatureSectionUpdate(event?: void | SignatureSectionUpdateEvent): void {
    if (event && event.action === 'signingDeadlineChanged') {
      const dueDate = this.getDueDateFromSigningDeadline(event.data);
      if (dueDate) {
        this.detailsSectionForm?.get('due_date')?.setValue(dueDate);
        this.updateDocumentDueDateFromValue(dueDate);
        return;
      }
    }

    this.updateDocument();
  }

  onDocumentTypeChange(documentType: IDocumentTypesDto): void {
    if (documentType?.id) {
      this._documentListingFacade.changeDocumentType(documentType.id, this.documentData.template.id);
    }
    this.updateDocument();
  }


  


  updateDueDateDisabled(): void {
    const status = this.documentData?.template_details?.status;
    this.isDueDateDisabled = !this.hasFullAccessToDocument || !DocumentDetailsComponent.allowedDueDateChangeStatuses.includes(status);

    if (this.isDueDateDisabled) {
      this.disableFlatpickr = [() => true];
    } else {
      this.disableFlatpickr = [];
    }
  }






  /**
   * Fetches and sets the document type layout settings based on the provided document type ID.
   * 
   * @param documentTypeId - The ID of the document type for which to fetch layout settings.
   * 
   * This method performs the following actions:
   * - Resets `documentTypeAllowedCategories` to null.
   * - If a valid `documentTypeId` is provided, it fetches the document section layout settings
   *   using `documentTypeService.fetchDocumentSectionLayoutSettings`.
   * - Subscribes to the response and updates `documentPreviewColumns` and `documentTypeAllowedCategories`
   *   based on the response.
   * - If an error occurs during the fetch, or if no `documentTypeId` is provided, it sets
   *   `documentPreviewColumns` to a default value.
   */
  getDocumentTypeLayoutSettings(documentTypeId: number): void {
    this.documentTypeAllowedCategories = null;
    if (documentTypeId) {
      this.documentTypeService.fetchDocumentSectionLayoutSettings(documentTypeId)
        .pipe(
          take(1),
        ).subscribe({
          next: (response: IFetchDocumentSectionLayoutSettings) => {
            this.documentPreviewColumns = response?.document_preview != null ? response.document_preview.filter(field => field.status == 1) : documentPreviewColumns;
            this.documentTypeAllowedCategories = response?.categories;
          },
          error: () => {
            this.documentPreviewColumns = documentPreviewColumns;
          }
        });
    } else {
      this.documentPreviewColumns = documentPreviewColumns;
    }
  }





  /**
   * Opens the document in full-screen mode.
   * Utilizes the DocumentHelperFacade service to redirect the current document data to a full-screen view.
   *
   * @returns {void}
   */
  openFullScreen(): void {
    const templateDetails = this.documentData?.template_details;
    const template = this.documentData?.template;

    const isNotUploaded = !templateDetails?.is_uploaded;
    const isTemplateDocument = templateDetails?.type === DocumentTypeEnum.TEMPLATEDOCUMENT;

    const nonEditableStatuses: DocumentStatusesEnum[] = [
      DocumentStatusesEnum.Uploaded,
      DocumentStatusesEnum.Signed,
      DocumentStatusesEnum.Rejected,
      DocumentStatusesEnum.Completed,
    ];
    const isEditableStatus = !nonEditableStatuses.includes(template?.status as DocumentStatusesEnum);

    const shouldRedirectToEditor = ((isNotUploaded && isEditableStatus) || isTemplateDocument) && this.canEditDocument;

    if (shouldRedirectToEditor) {
      this.router.navigate([`send-document/${template?.id}`], { state: { template_id: template?.id } });
    } else {
      this._documentHelperFacade.redirectToFullScreen(this.documentData);
    }
  }





  /**
   * Opens the AddToContractControlModalComponent modal.
   * 
   * This method checks if the user has full access to the document by evaluating
   * the `hasFullAccessToDocument$` observable. If the user has full access, it 
   * opens the modal with the specified configuration options:
   * - `size`: Sets the modal size to large ('lg').
   * - `backdrop`: Disables the backdrop.
   * - `windowClass`: Applies the 'add-to-contract-control-modal' CSS class to the modal window.
   */
  openAddToContractControlModalComponent(): void {
    if (this.hasFullAccessToDocument) {
      const instance = this._modalService.open(AddToContractControlModalComponent, {
        size: 'lg',
        backdrop: false,
        windowClass: 'add-to-contract-control-modal'
      });
      if(instance){
          this._actions$
              .pipe(
                  ofType(DocumentListingActions.connectContractSuccess),
                  take(1),
                  takeUntil(merge(this.destroy$, instance.closed))
              )
              .subscribe(
                  {
                      next: () => {
                          instance.close();
                          this._globalService.toastrSuccess(this._translocoService.translate('Contact has been linked (Contract control section - contact box) - Contact has been linked'));
                      }
                  }
              )
      }
    }
  }



 /**
   * Toggles the visibility of the current document between private and public.
   * 
   * This method checks the current visibility of the document and switches it
   * to the opposite state. It then calls the `updateDocumentVisibility` method
   * from the `_documentsFacade` service to update the visibility in the backend.
   * 
   * @remarks
   * - The visibility is determined based on the `template_details.visibility` property.
   * - The document ID is retrieved from the `template.id` property.
   * 
   * @throws
   * This method assumes that `documentData` and its nested properties are defined.
   * Ensure that these properties are properly initialized before calling this method.
   */
  updateDocumentVisibility(): void {
    const visibility = this.documentData?.template_details?.visibility === VisibilityEnum.Private ? VisibilityEnum.Public : VisibilityEnum.Private;
    this._documentsFacade.updateDocumentVisibility(this.documentData?.template?.id, visibility);
  }





 
  /**
   * Updates the due date of the document by invoking the documents facade service.
   *
   * @param $event - The event triggered by the user interaction, typically from an input element.
   *                 The new due date is extracted from the value of the event's target.
   */
  updateDocumentDueDate($event: Event): void {
    this._documentsFacade.updateDocumentDate(this.documentData?.template.id, ($event?.target as HTMLInputElement)?.value);
  }

  private updateDocumentDueDateFromValue(dueDate: string): void {
    if (!dueDate) {
      return;
    }

    this._documentsFacade.updateDocumentDate(this.documentData?.template?.id, dueDate);
    this._actions$
      .pipe(
        ofType(DocumentListingActions.updateDocumentDueDateSucceeeded, DocumentListingActions.updateDocumentDueDateFailed),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe((action) => {
        if (action.type === DocumentListingActions.updateDocumentDueDateSucceeeded.type) {
          this.updateDocument();
        }
      });
  }

  private updateSigningReminderFromDueDate(dueDate: string): void {
    const parsedDueDate = this.parseDateValue(dueDate);
    if (!parsedDueDate) {
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    parsedDueDate.setHours(0, 0, 0, 0);

    const oneDayMs = 24 * 60 * 60 * 1000;
    const diffInDays = Math.max(0, Math.ceil((parsedDueDate.getTime() - today.getTime()) / oneDayMs));
    const reminderValue = String(diffInDays);

    if (this.signatureSectionForm?.contains('signing_deadline')) {
      this.signatureSectionForm.get('signing_deadline')?.setValue(reminderValue);
    }
  }

  private getDueDateFromSigningDeadline(signingDeadline: string): string | null {
    if (!signingDeadline) {
      return null;
    }

    const parsedDays = Number(signingDeadline);
    if (!Number.isFinite(parsedDays)) {
      return null;
    }

    const days = Math.max(0, Math.floor(parsedDays));
    const createdAt = this.documentData?.template?.created_at;
    if (!createdAt) {
      return null;
    }

    const baseDate = moment(createdAt);
    if (!baseDate.isValid()) {
      return null;
    }

    return baseDate.startOf('day').add(days, 'days').format('YYYY-MM-DD');
  }

  private parseDateValue(value: string): Date | null {
    if (!value) {
      return null;
    }
    // Parse strictly against known date formats to avoid locale-dependent Date parsing bugs.
    const parsed = moment(value, ['YYYY-MM-DD', 'YYYY/MM/DD', 'DD-MM-YYYY', 'DD/MM/YYYY', 'MM-DD-YYYY', 'MM/DD/YYYY'], true);
    return parsed.isValid() ? parsed.toDate() : null;
  }




 
  /**
   * Handles the addition of a new tag to the document.
   *
   * @param $event - The tag object to be added.
   * 
   * This method updates the document's tags by invoking the `updateDocumentTag` method
   * from the `_documentsFacade` service. It uses the document's template ID and the
   * provided tag as parameters.
   */
  onAdd($event: Tag): void {
    this._documentsFacade.updateDocumentTag(this.documentData?.template?.id, $event);
  }



 
  /**
   * Removes a specified tag from the list of tags and updates the document's tags in the backend.
   *
   * @param item - The tag to be removed. It is an object of type `Tag` containing the tag's value.
   *
   * This method performs the following actions:
   * 1. Filters out the specified tag from the `tags` array by comparing the `value` property.
   * 2. Calls the `removeTagFromDocument` method from the `_documentsFacade` service to update the backend,
   *    passing the document's template ID and the tag to be removed.
   */
  removeTag(item: Tag): void {
    this.tags = this.tags.filter(tag => tag.value !== item.value);
    this._documentsFacade.removeTagFromDocument(this.documentData?.template?.id, item);
  }




  /**
   * Navigate to linked contract
   * @param contractId 
   */
  navigateToLinkedContract(contractId: number) {
    this._documentHelperFacade.openWindow(`/#/contract-control/view/${contractId}`);
  }


  
  /**
   * Copies the document's shareable URL to the clipboard and displays a tooltip
   * to indicate the action. The tooltip is automatically closed after a delay.
   *
   * The URL is constructed using the application's base URL and the document's
   * template ID and document number.
   *
   * @remarks
   * - The tooltip is opened when the method is invoked and closed after 1 second.
   * - The URL is copied to the clipboard using the `_copyToClipBoard$` service.
   *
   * @example
   * // Assuming `environment.appUrl` is "https://example.com/" and
   * // `this.documentData` contains the necessary template and document details:
   * // URL copied: "https://example.com/shareable/{templateId}/{documentNumber}"
   *
   * @returns void
   */
  copyDocUrl(): void {
    this.tooltip.open();
    const url = `${environment.appUrl}shareable/${this.documentData.template.id}/${this.documentData.template_details.document_number}`;
    this._copyToClipBoard$.copy(url);
    this.timerSubscription = of(null).pipe(
      take(1),
      delay(1000)
    ).subscribe(() => {
      this.tooltip.close();
    });
  }




  ngOnDestroy(): void {
    if(this.timerSubscription){
      this.timerSubscription?.unsubscribe();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

}
